import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/* ---------------------------------------------------------------------------
 * Session token persistence.
 * We store the token in FOUR places so that at least one survives every known
 * Android/iOS PWA storage-eviction quirk:
 *   1. localStorage           - normal browsers
 *   2. sessionStorage         - fallback if localStorage cleared by browser
 *   3. em_session cookie      - survives most WebAPK cold restarts
 *   4. IndexedDB              - most persistent store on installed PWAs
 * Every successful API response rewrites all four so a single wipe self-heals
 * on the next network hit.
 * ------------------------------------------------------------------------- */
const TOKEN_KEY = "session_token";
const COOKIE_MAX_AGE_DAYS = 3650;

const writeCookie = (token) => {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie = `em_session=${encodeURIComponent(token)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  } catch (_) { /* ignore */ }
};
const clearCookie = () => {
  try { document.cookie = "em_session=; Path=/; Max-Age=0; SameSite=Lax"; } catch (_) { /* ignore */ }
};
const readCookie = () => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)em_session=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) { return null; }
};

/* Minimal IndexedDB wrapper (no library, no build cost). */
const IDB_NAME = "em_auth";
const IDB_STORE = "kv";
function openIdb() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}
const idbSet = async (key, val) => {
  try {
    const db = await openIdb();
    await new Promise((res, rej) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(val, key);
      tx.oncomplete = res;
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch (_) { /* ignore */ }
};
const idbGet = async (key) => {
  try {
    const db = await openIdb();
    const val = await new Promise((res, rej) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    db.close();
    return val || null;
  } catch (_) { return null; }
};
const idbDel = async (key) => {
  try {
    const db = await openIdb();
    await new Promise((res, rej) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = res;
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch (_) { /* ignore */ }
};

export const setAuthToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch (_) { /* ignore */ }
    try { sessionStorage.setItem(TOKEN_KEY, token); } catch (_) { /* ignore */ }
    writeCookie(token);
    idbSet(TOKEN_KEY, token);
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
    clearCookie();
    idbDel(TOKEN_KEY);
  }
};

/**
 * On page load, promote whichever token we can find into ALL four stores.
 * Exported as a Promise so AuthContext can await it before making the first
 * `/auth/me` call — otherwise a PWA that only kept its IndexedDB copy would
 * fire the initial request without a Bearer header and get logged out.
 */
export const authReady = (async () => {
  const fromSync = (() => {
    try { return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
  })();
  const fromCookie = readCookie();
  const fromIdb = await idbGet(TOKEN_KEY);
  const survivor = fromSync || fromCookie || fromIdb;
  if (survivor) setAuthToken(survivor);
})();

export const getStoredToken = () => {
  try {
    const l = localStorage.getItem(TOKEN_KEY);
    if (l) return l;
  } catch (_) { /* ignore */ }
  try {
    const s = sessionStorage.getItem(TOKEN_KEY);
    if (s) return s;
  } catch (_) { /* ignore */ }
  return readCookie();
  // IndexedDB is async-only, so we can't read it inside a sync interceptor —
  // it's used at boot (above) to promote the token back into sync stores.
};

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use((res) => {
  const t = getStoredToken();
  if (t) setAuthToken(t);
  return res;
});

export default api;
